import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaArrowRight, FaSearch, FaSitemap, FaCode, FaRocket,
  FaQuoteLeft, FaStar, FaChevronLeft, FaChevronRight,
  FaLaptopCode, FaUsers, FaBriefcase, FaHeart, FaChartLine,
} from 'react-icons/fa';
import { PROJECTS_DATA } from '../../data/projects';
import { getTestimonials } from '../../utils/api';
import {
  fadeInUp, fadeInLeft, scaleIn,
  staggerContainer, staggerContainerSlow, viewport,
} from '../../utils/animations';

/* ══════════════════════════════════════════════════════════════
   ABOUT SECTION — stats + company blurb
══════════════════════════════════════════════════════════════ */
const StatCard = ({ end, suffix, label, icon: Icon }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!started) return;
    let frame = 0;
    const total = 70;
    const id = setInterval(() => {
      frame++;
      const prog = 1 - Math.pow(1 - frame / total, 3);
      setCount(Math.floor(prog * end));
      if (frame >= total) { setCount(end); clearInterval(id); }
    }, 20);
    return () => clearInterval(id);
  }, [started, end]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewport}
      transition={{ duration: 0.6 }}
      onViewportEnter={() => setStarted(true)}
      className="group glass-card rounded-2xl p-6 text-center card-hover-lift shine-hover"
    >
      <div className="w-12 h-12 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center mx-auto mb-4 group-hover:border-gray-600 transition-colors duration-300">
        <Icon className="text-xl text-gray-400 group-hover:text-white transition-colors duration-300" />
      </div>
      <div className="text-4xl font-black text-white mb-1">{count}{suffix}</div>
      <div className="text-gray-500 text-sm">{label}</div>
    </motion.div>
  );
};

export const AboutSection = () => (
  <section className="relative py-24 lg:py-32 overflow-hidden">
    <div className="absolute inset-0 dot-pattern pointer-events-none" />
    <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black to-transparent pointer-events-none" />

    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        {/* Left */}
        <motion.div
          variants={fadeInLeft}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="space-y-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900/60 border border-gray-700 rounded-full">
            <FaHeart className="text-gray-400 text-xs" />
            <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">About Us</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
            A team obsessed with<br />
            <span className="animated-gradient-text">building great software</span>
          </h2>

          <p className="text-gray-400 leading-relaxed text-lg">
            We're a product engineering studio where senior engineers and designers work side-by-side
            with your team. No handoffs, no black boxes, just fast, transparent delivery of software
            that actually works.
          </p>

          <p className="text-gray-400 leading-relaxed">
            From early-stage startups to scale-ups modernising legacy systems, we've helped companies
            across 12 countries ship products that users love and businesses rely on.
          </p>

          <Link
            to="/about"
            className="group inline-flex items-center gap-2 px-7 py-3.5 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-0.5 shine-hover"
          >
            Our Story
            <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </motion.div>

        {/* Right – stats grid */}
        <motion.div
          variants={staggerContainerSlow}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="grid grid-cols-2 gap-4"
        >
          {[
            { end: 150, suffix: '+', label: 'Projects Delivered', icon: FaBriefcase },
            { end: 50,  suffix: '+', label: 'Happy Clients',      icon: FaUsers },
            { end: 5,   suffix: '+', label: 'Years Experience',   icon: FaStar },
            { end: 30,  suffix: '+', label: 'Team Members',       icon: FaCode },
          ].map((s, i) => (
            <motion.div key={i} variants={scaleIn}>
              <StatCard {...s} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);

/* ══════════════════════════════════════════════════════════════
   PROCESS SECTION — how we work
══════════════════════════════════════════════════════════════ */
const steps = [
  { num: '01', icon: FaSearch,  title: 'Discovery',    desc: 'We dive deep into your goals, users, and technical landscape to define a clear direction.' },
  { num: '02', icon: FaSitemap, title: 'Blueprint',    desc: 'Detailed architecture, wireframes, and a sprint-ready roadmap before a single line is written.' },
  { num: '03', icon: FaCode,    title: 'Build',        desc: 'Agile sprints with weekly demos, continuous integration, and transparent progress tracking.' },
  { num: '04', icon: FaRocket,  title: 'Launch & Grow', desc: 'Zero-downtime deploy, monitoring setup, and ongoing support to help you scale confidently.' },
];

export const ProcessSection = () => (
  <section className="relative py-24 lg:py-32 overflow-hidden bg-gradient-to-b from-gray-900/50 to-black/50">
    <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />

    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        className="text-center mb-16"
      >
        <motion.div variants={fadeInUp} className="inline-flex mb-6">
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 border border-gray-700 rounded-full badge-glow">
            <FaSitemap className="text-gray-400 text-xs" />
            <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">How We Work</span>
          </div>
        </motion.div>
        <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4">
          Our <span className="animated-gradient-text">Process</span>
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-gray-400 max-w-lg mx-auto">
          A lean, proven process that keeps you in the loop and ships quality on time.
        </motion.p>
      </motion.div>

      <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {/* Connector line */}
        <div className="hidden lg:block absolute top-14 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent pointer-events-none" />

        {steps.map((step, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewport}
            transition={{ delay: i * 0.15, duration: 0.65, ease: [0.25, 0.1, 0.25, 1] }}
            whileHover={{ y: -6, transition: { duration: 0.2 } }}
            className="group relative glass-card rounded-2xl p-8 text-center card-hover-lift shine-hover"
          >
            {/* Step number */}
            <div className="relative inline-flex w-20 h-20 rounded-full bg-gray-900/70 border border-gray-700 items-center justify-center mb-6 group-hover:border-gray-500 transition-all duration-300 mx-auto animate-pulse-glow">
              <step.icon className="text-2xl text-gray-400 group-hover:text-white transition-colors duration-300" />
              <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gradient-to-br from-gray-500 to-gray-800 border border-gray-600 text-xs font-black text-white flex items-center justify-center">
                {step.num}
              </span>
            </div>
            <h3 className="text-white font-bold text-xl mb-3">{step.title}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   PROJECTS SECTION — featured projects (rich cards)
────────────────────────────────────────────────────────────── */
const HOME_CATEGORY_COLORS = {
  'Industry 5.0': 'from-violet-600 to-purple-800',
  CleanTech: 'from-emerald-500 to-teal-700',
  HealthTech: 'from-sky-500 to-blue-700',
  FinTech: 'from-amber-500 to-orange-700',
  EdTech: 'from-pink-500 to-rose-700',
  Logistics: 'from-cyan-500 to-indigo-700',
};
const getHomeCategoryGradient = (cat) => HOME_CATEGORY_COLORS[cat] || 'from-gray-600 to-gray-800';

export const ProjectsSection = () => {
  const featured = PROJECTS_DATA.filter((p) => p.featured).slice(0, 3);

  return (
    <section className="relative py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6"
        >
          <div>
            <motion.div variants={fadeInUp} className="inline-flex mb-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 border border-gray-700 rounded-full badge-glow">
                <FaLaptopCode className="text-gray-400 text-xs" />
                <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Featured Work</span>
              </div>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white">
              Recent <span className="animated-gradient-text">Projects</span>
            </motion.h2>
          </div>
          <motion.div variants={fadeInUp}>
            <Link
              to="/projects"
              className="group inline-flex items-center gap-2 px-6 py-3 border border-gray-700 text-gray-400 font-semibold rounded-xl hover:border-gray-500 hover:text-white transition-all duration-300"
            >
              View All <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          variants={staggerContainerSlow}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
        >
          {featured.map((p, i) => (
            <motion.div
              key={p._id}
              variants={scaleIn}
              whileHover={{ y: -8, transition: { duration: 0.22 } }}
              className="group glass-card rounded-2xl overflow-hidden card-hover-lift"
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden bg-gray-900">
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/40 to-transparent" />
                <div className={`absolute inset-0 bg-gradient-to-br ${getHomeCategoryGradient(p.category)} opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />

                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest bg-black/50 backdrop-blur-sm border border-white/10 text-gray-300 rounded-full">
                    {p.category}
                  </span>
                  {p.featured && (
                    <span className="flex items-center gap-1 px-3 py-1 text-[10px] font-bold uppercase tracking-widest bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-full">
                      <FaStar className="text-[8px] text-yellow-400" /> Featured
                    </span>
                  )}
                </div>

                {p.metrics?.[0] && (
                  <div className="absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-black/60 backdrop-blur-sm border border-white/10 rounded-full">
                    <FaChartLine className="text-emerald-400 text-[10px]" />
                    <span className="text-white text-xs font-bold">{p.metrics[0].value}</span>
                    <span className="text-gray-400 text-[10px]">{p.metrics[0].label}</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-white font-bold text-lg mb-1.5 group-hover:text-gray-100 transition-colors duration-300 leading-snug">{p.title}</h3>
                <p className="text-gray-500 text-xs font-medium italic mb-3">{p.tagline}</p>
                <p className="text-gray-400 text-sm leading-relaxed mb-5 line-clamp-2">{p.description}</p>
                <div className="flex flex-wrap gap-1.5 mb-6">
                  {p.techStack.slice(0, 4).map((t, j) => (
                    <span key={j} className="px-2 py-0.5 text-[11px] bg-gray-800/80 border border-gray-700/60 text-gray-400 rounded-md">{t}</span>
                  ))}
                  {p.techStack.length > 4 && (
                    <span className="px-2 py-0.5 text-[11px] bg-gray-800/80 border border-gray-700/60 text-gray-500 rounded-md">+{p.techStack.length - 4}</span>
                  )}
                </div>
                <Link to={`/projects/${p.slug}`} className="inline-flex items-center gap-2 text-sm font-semibold text-gray-400 group-hover:text-white transition-colors duration-300">
                  View Case Study <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════════════
   TESTIMONIALS SECTION — rotating quotes
══════════════════════════════════════════════════════════════ */
const DEMO_TESTIMONIALS = [
  { _id: '1', name: 'Saurabh Sharma', role: 'CTO', company: 'Sqaby Technologies', rating: 5, content: 'Zynexis exceeded every expectation. Their engineering team tackled our complex infrastructure challenges with remarkable precision and delivered a product that scaled beautifully from day one. A partner we trust completely.' },
  { _id: '2', name: 'Amy McKinsey', role: 'Managing Director', company: 'Blitzgate Capital', rating: 5, content: 'From scoping to launch, Zynexis team demonstrated an exceptional level of professionalism. They understood our domain quickly, asked the right questions, and delivered a platform that our clients genuinely love.' },
  { _id: '3', name: 'Appalnaidu Chandaka', role: 'CTO', company: 'Saga', rating: 5, content: 'What set Zynexis apart was their ability to think like a product team, not just engineers. They challenged assumptions, proposed smarter solutions, and shipped on time. Exactly the kind of partner a startup needs.' },
];

export const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const timerRef = useRef(null);

  useEffect(() => {
    getTestimonials({ active: true })
      .then(res => {
        const data = res?.data?.data || res?.data || [];
        setTestimonials(Array.isArray(data) && data.length ? data : DEMO_TESTIMONIALS);
      })
      .catch(() => setTestimonials(DEMO_TESTIMONIALS));
  }, []);

  const list = testimonials.length ? testimonials : DEMO_TESTIMONIALS;

  const go = (dir) => {
    setDirection(dir);
    setCurrent(c => (c + dir + list.length) % list.length);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => autoAdvance(), 5000);
  };
  const autoAdvance = () => { setDirection(1); setCurrent(c => (c + 1) % list.length); };

  useEffect(() => {
    timerRef.current = setInterval(autoAdvance, 5000);
    return () => clearInterval(timerRef.current);
  }, [list.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const item = list[current];

  const slideVariants = {
    enter: d => ({ x: d > 0 ? 80 : -80, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: d => ({ x: d > 0 ? -80 : 80, opacity: 0 }),
  };

  return (
    <section className="relative py-24 lg:py-32 overflow-hidden bg-transparent">
      <div className="absolute inset-0 grid-pattern opacity-25 pointer-events-none" />
      <div className="spotlight w-[500px] h-[500px] top-1/2 left-1/2" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="text-center mb-16"
        >
          <motion.div variants={fadeInUp} className="inline-flex mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 border border-gray-700 rounded-full badge-glow">
              <FaStar className="text-gray-400 text-xs" />
              <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Client Love</span>
            </div>
          </motion.div>
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4">
            What Our <span className="animated-gradient-text">Clients Say</span>
          </motion.h2>
        </motion.div>

        {/* Testimonial slider */}
        <div className="max-w-3xl mx-auto">
          <div className="relative overflow-hidden">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={current}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
                className="glass-card rounded-3xl p-10 md:p-12 text-center"
              >
                <FaQuoteLeft className="text-3xl text-gray-600 mx-auto mb-6" />

                {/* Stars */}
                <div className="flex justify-center gap-1 mb-6">
                  {[...Array(item.rating || 5)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-500/70 text-sm" />
                  ))}
                </div>

                <p className="text-gray-300 text-lg md:text-xl leading-relaxed mb-8 italic">
                  "{item.content}"
                </p>

                <div className="flex items-center justify-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 border border-gray-600 flex items-center justify-center text-white font-bold text-lg">
                    {item.name?.charAt(0)}
                  </div>
                  <div className="text-left">
                    <div className="text-white font-bold">{item.name}</div>
                    <div className="text-gray-500 text-sm">{item.role}{item.company && `, ${item.company}`}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-6 mt-8">
            <button
              onClick={() => go(-1)}
              className="w-10 h-10 rounded-full glass-card border border-gray-700 flex items-center justify-center text-gray-400 hover:text-white hover:border-gray-500 transition-all duration-300"
            >
              <FaChevronLeft className="text-sm" />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {list.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i); }}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === current ? 'w-8 bg-white' : 'w-2 bg-gray-700 hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={() => go(1)}
              className="w-10 h-10 rounded-full glass-card border border-gray-700 flex items-center justify-center text-gray-400 hover:text-white hover:border-gray-500 transition-all duration-300"
            >
              <FaChevronRight className="text-sm" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════════════
   CTA SECTION — animated call-to-action
══════════════════════════════════════════════════════════════ */
export const CTASection = () => (
  <section className="relative py-24 lg:py-36 overflow-hidden">
    {/* Layered backgrounds */}
    <div className="absolute inset-0 bg-gradient-to-b from-gray-950/60 to-black/60 pointer-events-none" />
    <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />

    {/* Animated orbs */}
    <div className="absolute top-10 left-1/4 w-96 h-96 rounded-full bg-gray-700/10 blur-3xl animate-scale-pulse pointer-events-none" />
    <div className="absolute bottom-10 right-1/4 w-80 h-80 rounded-full bg-gray-600/10 blur-3xl animate-scale-pulse pointer-events-none" style={{ animationDelay: '1.5s' }} />
    <div className="spotlight w-[600px] h-[600px] top-1/2 left-1/2" />

    {/* Floating particles */}
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1.5 h-1.5 rounded-full bg-gray-500/40 animate-float"
        style={{
          left: `${10 + i * 11}%`,
          top: `${20 + (i % 3) * 25}%`,
          animationDelay: `${i * 0.4}s`,
          animationDuration: `${5 + i}s`,
        }}
      />
    ))}

    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        className="max-w-4xl mx-auto"
      >
        <motion.div variants={scaleIn} className="inline-flex mb-8">
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 border border-gray-700 rounded-full badge-glow">
            <FaRocket className="text-gray-400 text-xs animate-float" />
            <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Let's Build Together</span>
          </div>
        </motion.div>

        <motion.h2 variants={fadeInUp} className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
          Ready to turn your idea
          <br />
          <span className="animated-gradient-text">into reality?</span>
        </motion.h2>

        <motion.p variants={fadeInUp} className="text-gray-400 text-xl md:text-2xl mb-12 max-w-2xl mx-auto leading-relaxed">
          Whether you're starting from scratch or scaling an existing product,
          we'd love to be part of your journey.
        </motion.p>

        <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
          <Link
            to="/contact"
            className="group relative flex items-center gap-3 px-10 py-4 bg-white text-gray-950 font-bold text-lg rounded-xl overflow-hidden hover:bg-gray-100 transition-all duration-300 hover:-translate-y-1 shine-hover"
          >
            Start a Project
            <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          <Link
            to="/services"
            className="group flex items-center gap-3 px-10 py-4 glass-card border border-gray-700 text-gray-300 font-semibold text-lg rounded-xl hover:border-gray-500 hover:text-white transition-all duration-300 hover:-translate-y-1"
          >
            Explore Services
          </Link>
        </motion.div>

        {/* Trust badges */}
        {/* <motion.div
          variants={fadeInUp}
          className="flex flex-wrap justify-center gap-8 mt-16 pt-10 border-t border-gray-800/50"
        >
          {[
            { icon: '⚡', text: 'Fast delivery' },
            { icon: '🔒', text: 'NDA included' },
            { icon: '🎯', text: 'Fixed-price options' },
            { icon: '🌍', text: 'Global team' },
          ].map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewport}
              transition={{ delay: i * 0.1 }}
              className="flex items-center gap-2 text-gray-500 text-sm"
            >
              <span>{b.icon}</span>
              <span>{b.text}</span>
            </motion.div>
          ))}
        </motion.div> */}
      </motion.div>
    </div>

    {/* Bottom fade */}
    <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black to-transparent pointer-events-none" />
  </section>
);

const HomeComponents = {
  AboutSection,
  ProcessSection,
  ProjectsSection,
  TestimonialsSection,
  CTASection,
};
export default HomeComponents;


